import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeOnboardingSiteUrl, stripHttpProtocol } from '../utils';
import { useEffect, useState } from 'react';
import { Checkbox, Input, TextArea } from '@crazyegginc/hatch';
import { addDays, format } from 'date-fns';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Header } from '../components/ui/Header';
import { Footer } from '../components/ui/Footer';
import WordPressIcon from '@crazyegginc/hatch/dist/images/logo-wordpress-square.svg';
import GoogleTagManagerIcon from '@crazyegginc/hatch/dist/images/logo-gtm-square.svg';
// import ShopifyIcon from '@crazyegginc/hatch/dist/images/logo-shopify-square.svg';
import WixIcon from '@crazyegginc/hatch/dist/images/logo-wix-square.svg';
import SquarespaceIcon from '@crazyegginc/hatch/dist/images/logo-squarespace-square.svg';
import DrupalIcon from '@crazyegginc/hatch/dist/images/logo-drupal-square.svg';
import MagentoIcon from '@crazyegginc/hatch/dist/images/logo-magento-square.svg';
import JoomlaIcon from '@crazyegginc/hatch/dist/images/logo-joomla-square.svg';
import SegmentIcon from '@crazyegginc/hatch/dist/images/logo-segment-square.svg';
import { ReactComponent as CodeIcon } from '@crazyegginc/hatch/dist/images/icon-code-outline.svg';
import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-circle-filled.svg';
import { useAuthContext, useMutation, useNotifications } from '/src/hooks';
import { ONBOARDING_FLOWS, TECH_RECOMMENDATIONS, normalizeTechRecommendation } from '../constants';
import { Card } from '../components/ui/Card';
import { sendInstallInstructionsEmailMutation } from '/src/features/installation/mutations';
import { upsertUserOnboardingMutation } from '/src/features/onboarding/mutations';
import { addSiteMutation } from '/src/features/options/mutations';
import { createSnapshotsMutation } from '/src/features/snapshots/mutations';
import { completeOnboarding } from '../completion';
import { useOnboardingContext } from '../hooks/useOnboardingContext';
import { isSemrush } from '/src/utils/semrush';

const instructionModules = import.meta.glob('../instructions/*.jsx', { eager: true });

const techOptions = [
  { key: 'manual', label: 'Install Manually', icon: CodeIcon, iconClassName: 'fill-current text-cadet-blue-500' },
  { key: 'drupal', label: 'Drupal', icon: DrupalIcon },
  { key: 'gtm', label: 'Google Tag Manager', icon: GoogleTagManagerIcon },
  { key: 'magento', label: 'Magento 1', icon: MagentoIcon },
  { key: 'magento2', label: 'Magento 2', icon: MagentoIcon },
  { key: 'joomla', label: 'Joomla', icon: JoomlaIcon },
  { key: 'segment', label: 'Segment', icon: SegmentIcon },
  // { key: 'shopify', label: 'Shopify', icon: ShopifyIcon },
  { key: 'wix', label: 'Wix', icon: WixIcon },
  { key: 'squarespace', label: 'Squarespace', icon: SquarespaceIcon },
  { key: 'wordpress', label: 'WordPress', icon: WordPressIcon },
];

const instructionComponents = Object.entries(instructionModules).reduce((acc, [path, module]) => {
  const key = path.split('/').pop().replace('.jsx', '');
  const instructionComponent = Object.values(module).find((value) => typeof value === 'function');

  if (instructionComponent) {
    acc[key] = instructionComponent;
  }

  return acc;
}, {});

const metaMapping = ({ accountNumber }) => ({
  manual: {
    title: 'Install Crazy Egg tracking script',
    subtitle: 'The tracking script lets Crazy Egg record visitor behavior on your site.',
  },
  wordpress: {
    title: 'Install Crazy Egg on your Wordpress site',
    subtitle: null,
  },
  gtm: {
    title: 'Connect Crazy Egg with Google Tag Manager',
    subtitle: (
      <p className="text-xl font-normal leading-[30px] text-black-pearl-500">
        Once you’re prompted for your account number, enter <strong>{accountNumber}.</strong>
      </p>
    ),
  },
  // shopify: {
  //   title: 'Connect Crazy Egg with Shopify',
  //   subtitle: `You’ll need to install the Crazy Egg app found in the Shopify App Store.`,
  // },
  wix: {
    title: 'Connect Crazy Egg with Wix',
    subtitle: `This feature is only available for sites with a connected domain.`,
  },
  squarespace: {
    title: 'Connect Crazy Egg with Squarespace',
    subtitle: null,
  },
  drupal: {
    title: 'Connect Crazy Egg with Drupal',
    subtitle: (
      <p className="text-xl font-normal leading-[30px] text-black-pearl-500">
        Once you’re prompted for your account number, enter <strong>{accountNumber}.</strong>
      </p>
    ),
  },
  magento: {
    title: 'Connect Crazy Egg with Magento 1',
    subtitle: null,
  },
  magento2: {
    title: 'Connect Crazy Egg with Magento 2',
    subtitle: null,
  },
  joomla: {
    title: 'Connect Crazy Egg with Joomla',
    subtitle: (
      <p className="text-xl font-normal leading-[30px] text-black-pearl-500">
        Once you’re prompted for your account number, enter <strong>{accountNumber}.</strong>
      </p>
    ),
  },
  segment: {
    title: 'Connect Crazy Egg with Segment',
    subtitle: null,
  },
});

const contactDeveloperValidationSchema = yup.object().shape({
  developerEmail: yup
    .string()
    .required('Please provide an email address.')
    .email('Please provide a valid email address.'),
  site: yup
    .string()
    .required('Please provide a site domain.')
    .test('domain', 'Please provide a valid domain like example.com.', (value) => isDomainLike(value)),
  message: yup.string(),
  ccMe: yup.boolean(),
});

function isDomainLike(value) {
  if (!value) {
    return false;
  }

  const host = stripHttpProtocol(value.trim().toLowerCase()).split('/')[0];

  if (!host || host.includes(' ') || host.startsWith('.') || host.endsWith('.')) {
    return false;
  }

  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(host);
}

export function InstructionsStep() {
  const { currentUser, currentAccount } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutateAsync: upsertUserOnboarding } = useMutation(upsertUserOnboardingMutation);

  const { siteUrl, flow, recommendation, tech, view } = useOnboardingContext(currentUser.onboarding.flow);
  const activeRecommendation = normalizeTechRecommendation(tech ?? recommendation) ?? TECH_RECOMMENDATIONS.NONE;
  const selectedTechOption = techOptions.find((option) => option.key === activeRecommendation) ?? null;
  const mappedMeta = metaMapping({ accountNumber: currentAccount?.id ?? 0 });
  const meta = mappedMeta[activeRecommendation] ?? mappedMeta.manual;
  const SelectedInstructions = instructionComponents[activeRecommendation] ?? instructionComponents.manual;

  const isHeatmapFlow = flow === ONBOARDING_FLOWS.HEATMAPS;
  const isContactDeveloperView = view === 'contact';

  const updateSearchParams = (upserts) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    Object.entries(upserts).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        nextSearchParams.delete(key);
        return;
      }

      nextSearchParams.set(key, String(value));
    });

    setSearchParams(nextSearchParams);
  };

  const handleSelectTech = (nextTech) => {
    updateSearchParams({ tech: nextTech });

    void upsertUserOnboarding({ siteTech: nextTech }).catch(() => {
      // noop: tech preference update should not block onboarding flow
    });
  };

  const handleContactDeveloper = () => {
    updateSearchParams({
      tech: activeRecommendation,
      view: 'contact',
    });
  };

  useEffect(() => {
    if (tech && !instructionComponents[tech]) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('tech');
      setSearchParams(nextSearchParams);
    }
  }, [searchParams, setSearchParams, tech]);

  return (
    <>
      <Header currentStep={2} totalSteps={3} />

      <div className="mx-auto mb-12 flex w-full max-w-[720px] flex-col px-6 pt-6 sm:pt-0 md:px-0">
        {isContactDeveloperView ? (
          <ContactDeveloper siteUrl={siteUrl} />
        ) : (
          <>
            <div className="flex flex-col sm:items-center gap-2 sm:gap-3 sm:self-center">
              <h1 className="!m-0 mb-4 sm:text-center text-2xl sm:text-[28px] font-bold leading-9 sm:leading-[40px] text-black-pearl-500 md:text-[32px] md:leading-[48px]">
                {meta.title}
              </h1>
              {meta.subtitle ? (
                typeof meta.subtitle === 'string' ? (
                  <p className="sm:text-center text-base sm:text-xl font-normal leading-6 sm:leading-[30px] text-black-pearl-500">
                    {meta.subtitle}
                  </p>
                ) : (
                  meta.subtitle
                )
              ) : null}
            </div>

            {SelectedInstructions ? (
              <SelectedInstructions
                techOptions={techOptions}
                selectedTechOption={selectedTechOption}
                onSelectTech={handleSelectTech}
                onContactDeveloper={handleContactDeveloper}
              />
            ) : null}

            <Footer
              canBack={isHeatmapFlow}
              canSkip
              nextText="Check Installation"
              backTo="/onboarding/setup"
              nextTo="/onboarding/check-install"
            />
          </>
        )}
      </div>
    </>
  );
}

function ContactDeveloper({ siteUrl = '' }) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [sentSiteUrl, setSentSiteUrl] = useState('');
  const [searchParams] = useSearchParams();
  const forceSuccess = searchParams.get('success') === 'true';
  const notifications = useNotifications();
  const deadline = format(addDays(new Date(), 3), 'MMM do');
  const { mutate: sendInstallInstructionsEmail, isPending } = useMutation(sendInstallInstructionsEmailMutation);

  if (isSuccess) {
    return <ContactDeveloperSuccess siteUrl={sentSiteUrl} />;
  }

  return (
    <Formik
      initialValues={{
        developerEmail: '',
        site: siteUrl,
        message: `Please install the Crazy Egg tracking script on ${siteUrl} by ${deadline} so that we can start tracking data.\n\nThanks!`,
        ccMe: true,
      }}
      validationSchema={contactDeveloperValidationSchema}
      validateOnMount
      onSubmit={(values) => {
        setSentSiteUrl(values.site);

        if (forceSuccess) {
          setIsSuccess(true);
          return;
        }

        sendInstallInstructionsEmail(
          {
            email: {
              ccMe: isSemrush ? false : values.ccMe,
              developerEmail: values.developerEmail,
              message: values.message,
              siteUrl: values.site,
            },
          },
          {
            onSuccess: (data) => {
              if (data.sendInstallInstructionsEmail?.success === true) {
                setIsSuccess(true);
              }
            },
            onError: () => {
              notifications.error({ content: 'Oops! Email sending failed. Please try again later.', timeout: 3000 });
            },
          },
        );
      }}
    >
      {({ touched, values, errors, isValid, handleChange, handleBlur, submitForm }) => (
        <>
          <div className="flex flex-col sm:items-center gap-2 sm:gap-3 sm:self-center">
            <h1 className="!m-0 mb-4 sm:text-center text-2xl sm:text-[28px] font-bold leading-9 sm:leading-[40px] text-black-pearl-500 md:text-[32px] md:leading-[48px]">
              Send tracking script to my developer
            </h1>
            <p className="sm:text-center text-base sm:text-xl font-normal leading-6 sm:leading-[30px] text-black-pearl-500">
              Tell us about your web developer and we&apos;ll send them the tracking script.
            </p>
          </div>

          <Card className="flex flex-col gap-5 mt-5 mb-6">
            <Input
              name="developerEmail"
              id="onboarding_developer_email"
              label={
                <span className="text-sm font-semibold leading-5 text-black-pearl-500">Web developer&apos;s email</span>
              }
              value={values.developerEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              validationIcon={touched.developerEmail ? !errors.developerEmail : null}
              error={touched.developerEmail ? errors.developerEmail : null}
              className="h-10 py-[11px] px-4 w-full"
              placeholder="e.g. johndoe@email.com"
            />

            <Input
              name="site"
              id="onboarding_site"
              label={<span className="text-sm font-semibold leading-5 text-black-pearl-500">Site</span>}
              value={values.site}
              onChange={handleChange}
              onBlur={handleBlur}
              validationIcon={touched.site ? !errors.site : null}
              error={touched.site ? errors.site : null}
              className="h-10 py-[11px] px-4 w-full"
            />

            <TextArea
              rows={4}
              name="message"
              id="onboarding_message"
              label={
                <span className="text-sm font-semibold leading-5 text-black-pearl-500">
                  Send a personalized message
                </span>
              }
              value={values.message}
              onChange={handleChange}
              onBlur={handleBlur}
              className="py-[11px] px-4 w-full resize-y"
            />

            {!isSemrush ? (
              <div className="[&>div>div>div:first-child]:-ml-[5px]">
                <Checkbox
                  label={<span className="leading-5 text-black-pearl-500">CC&apos; me on this email</span>}
                  id="onboarding_cc_me"
                  name="ccMe"
                  checked={values.ccMe}
                  onChange={handleChange}
                  labelClass="!ml-[3px]"
                />
              </div>
            ) : null}
          </Card>

          <Footer
            canBack
            canSkip
            nextText={isPending ? 'Sending...' : 'Send Tracking Script'}
            nextOnClick={submitForm}
            backTo="/onboarding/instructions"
            backUpserts={{ view: null }}
            nextProps={{ disabled: !isValid || isPending }}
          />
        </>
      )}
    </Formik>
  );
}

function ContactDeveloperSuccess({ siteUrl = '' }) {
  const { currentUser } = useAuthContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [isCompleting, setIsCompleting] = useState(false);
  const { mutateAsync: upsertUserOnboarding } = useMutation(upsertUserOnboardingMutation);
  const createSite = useMutation(addSiteMutation);
  const createSnapshots = useMutation(createSnapshotsMutation);

  const {
    flow,
    isHeatmapFlow,
    completionUrl: contextCompletionUrl,
    heatmapUrl,
  } = useOnboardingContext(currentUser.onboarding.flow);
  const submittedSiteUrl = normalizeOnboardingSiteUrl(siteUrl);
  const completionUrl = isHeatmapFlow && heatmapUrl ? contextCompletionUrl : submittedSiteUrl || contextCompletionUrl;

  const handleComplete = async () => {
    if (isCompleting) {
      return;
    }

    setIsCompleting(true);
    await completeOnboarding({
      completionUrl,
      isHeatmapFlow,
      flow,
      completedAt: currentUser.onboarding.completedAt,
      createSite,
      createSnapshots,
      upsertUserOnboarding,
      navigate,
      notifications,
      queryClient,
    });
    setIsCompleting(false);
  };

  return (
    <>
      <div className="flex flex-col sm:items-center gap-2 sm:gap-3 sm:self-center">
        <h1 className="!m-0 mb-4 sm:text-center text-2xl sm:text-[28px] font-bold leading-9 sm:leading-[40px] text-black-pearl-500 md:text-[32px] md:leading-[48px]">
          Send tracking script to my developer
        </h1>
        <p className="sm:text-center text-base sm:text-xl font-normal leading-6 sm:leading-[30px] text-black-pearl-500">
          Tell us about your web developer and we&apos;ll send them the tracking script.
        </p>
      </div>

      <Card className="flex flex-col gap-5 items-center mt-5 mb-6 text-center">
        <TickIcon className="h-[65px] w-[65px] fill-current text-lima-500" />
        <span className="text-base font-bold leading-6 text-lima-500">Email sent!</span>
      </Card>

      <Footer
        nextText={isCompleting ? 'Setting up...' : 'Go to Dashboard'}
        nextOnClick={handleComplete}
        nextProps={{ disabled: isCompleting }}
      />
    </>
  );
}
